import { Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CAppSubTitle from '../../../../app/components/CAppSubTitle';
import AppFormatDate from '../../../../app/features/format/AppFormatDate';
import AppFormatPercent from '../../../../app/features/format/AppFormatPercent';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import IFund from '../../interfaces/fund/IFund';
import investmentTypeArticleLang from '../../features/fund-type/langs/investmentTypeArticleLang';
import CFundInformationBlock from '../../features/fund/components/CFundInformationBlock';
import useInvestFundInformations from '../../hooks/fund/useInvestFundInformations';
import IFundFilters from '../../interfaces/fund/IFundFilters';
import investmentFundInformationsLang from '../../langs/investmentFundInformationsLang';
import investmentFundLang from '../../langs/investmentFundLang';
import investmentTypeDomainLang from '../../features/fund-type/langs/investmentTypeDomainLang';
import langInvestWordLang from '../../langs/langInvestWordLang';
import IFundType, { KFundInformations } from '../../interfaces/expertise/IFundType';
import useInvestExpertises from '../../hooks/expertise/useInvestExpertises';
import AppFormatX from '../../../../app/features/format/AppFormatX';
import { Box, SxProps } from '@mui/system';
import { INVEST_FUND_TAG_LIST } from '../../types/fund/TInvestFundLabelTag';

type VInvestFundInformationsProps = {
	fund: IFund;
	filters: IFundFilters;
};

const sxImgLabels = {
	height: '42px',
	width: '42px',
} as SxProps;

const VInvestFundInformations = (props: VInvestFundInformationsProps) => {
	const { fund } = props;

	const { t } = useTranslation();

	const investExpertises = useInvestExpertises();

	const { fundInformations } = useInvestFundInformations(fund.id);

	const [fundType, setFundType] = useState<IFundType | null>(null);
	useEffect(() => {
		if (fund && investExpertises) {
			setFundType(investExpertises.find((x) => x.tag === fund.typeTag) ?? null);
		} else {
			setFundType(null);
		}
	}, [fund, investExpertises]);

	const getFundInformationBlock = (key: KFundInformations) => {
		if (!fundType || !fundInformations) return null;
		switch (key) {
			case 'general':
				return (
					<CFundInformationBlock
						id="fund-information-general-block"
						title={t(investmentFundInformationsLang.title_general.tag)}
						icon="fund_informations_general"
						items={[
							...(fundType.fundInformations?.general?.includes('juridical_status')
								? [
										{
											id: 'juridical_status',
											label: t(investmentFundInformationsLang.item_juridical_status.tag),
											value:
												fundInformations.general.juridicalStatus ??
												t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('isin')
								? [
										{
											id: 'isin',
											label: t(investmentFundInformationsLang.item_isin.tag),
											value: fundInformations.general.isin ?? t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('currency')
								? [
										{
											id: 'currency',
											label: t(investmentFundInformationsLang.item_currency.tag),
											value: fundInformations.general.currency ?? t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('hurdle')
								? [
										{
											id: 'hurdle',
											label: t(investmentFundInformationsLang.item_hurdle.tag),
											value: (
												<AppFormatX
													value={fundInformations.general.hurdle}
													addSpace
													textIfNull={t(langInvestWordLang.nc.tag)}
												/>
											),
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('targeted_performance')
								? [
										{
											id: 'targeted_performance',
											label: t(investmentFundInformationsLang.item_targeted_performance.tag),
											value: (
												<AppFormatPercent
													value={fundInformations.general.targetedPerformance}
													addSpace
													textIfNull={t(langInvestWordLang.nc.tag)}
												/>
											),
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('admin_fees_num')
								? [
										{
											id: 'admin_fees_num',
											label: t(investmentFundInformationsLang.item_fund_admin_fees.tag),
											value: (
												<>
													<AppFormatPercent
														value={fundInformations.general.fundAdminFeesNum}
														addSpace
														textIfNull={t(langInvestWordLang.nc.tag)}
													/>{' '}
													{fundInformations.general.fundAdminFeesNum
														? t(
																investmentFundInformationsLang
																	.item_fund_admin_fees_of_actif_net.tag,
														  )
														: ''}
												</>
											),
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('admin_fees_text')
								? [
										{
											id: 'admin_fees_text',
											label: t(investmentFundInformationsLang.item_fund_admin_fees.tag),
											value: fundInformations.general.fundAdminFeesText
												? fundInformations.general.fundAdminFeesText
												: '',
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('distribution_frequency')
								? [
										{
											id: 'distribution_frequency',
											label: t(investmentFundInformationsLang.item_distribution_frequency.tag),
											value: (
												<>
													{fundInformations.general.distributionFrequencyPerYear === 12
														? t(
																investmentFundInformationsLang
																	.item_distribution_frequency_annual.tag,
														  )
														: fundInformations.general.distributionFrequencyPerYear === 6
														? t(
																investmentFundInformationsLang
																	.item_distribution_frequency_biannual.tag,
														  )
														: fundInformations.general.distributionFrequencyPerYear === 3
														? t(
																investmentFundInformationsLang
																	.item_distribution_frequency_quarterly.tag,
														  )
														: (fundInformations.general.distributionFrequencyPerYear ?? 0) >
														  0
														? t(
																investmentFundInformationsLang
																	.item_distribution_frequency_x_months.tag,
																{
																	count: fundInformations.general
																		.distributionFrequencyPerYear,
																},
														  )
														: t(langInvestWordLang.nc.tag)}
												</>
											),
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('creation_date')
								? [
										{
											id: 'general_creation_date',
											label: t(investmentFundInformationsLang.item_creation_date.tag),
											value: <AppFormatDate date={fundInformations.general.creationDate} />,
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('launching_date') &&
							fundInformations.general.creationDate
								? [
										{
											id: 'general_launching_date',
											label: t(investmentFundInformationsLang.item_launching_date.tag),
											value: <AppFormatDate date={fundInformations.general.creationDate} />,
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('agreement_date')
								? [
										{
											id: 'general_agreement_date',
											label: t(investmentFundInformationsLang.item_agreement_date.tag),
											value: <AppFormatDate date={fundInformations.general.accreditationDate} />,
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('classification_AMF')
								? [
										{
											id: 'general_classification_AMF',
											label: t(investmentFundInformationsLang.item_classification_AMF.tag),
											value:
												fundInformations.general.amfClassification ??
												t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.general?.includes('risk_level')
								? [
										{
											id: 'general_risk_level',
											label: t(investmentFundInformationsLang.item_risk_level.tag),
											value: fundInformations.general.risk ?? t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
						]}
					/>
				);
			case 'planning':
				return (
					<CFundInformationBlock
						id="fund-information-planning-block"
						title={t(investmentFundInformationsLang.title_planning.tag)}
						icon="fund_informations_planning"
						items={[
							...(fundType.fundInformations?.planning?.includes('creation_date')
								? [
										{
											id: 'creation_date',
											label: t(investmentFundInformationsLang.item_creation_date.tag),
											value: <AppFormatDate date={fundInformations.planning?.creationDate} />,
										},
								  ]
								: []),
							...(fundType.fundInformations?.planning?.includes('subscription_end_date')
								? [
										{
											id: 'subscription_end_date',
											label: t(investmentFundInformationsLang.item_subscription_end_date.tag),
											value: (
												<AppFormatDate date={fundInformations.planning?.subscriptionEndDate} />
											),
										},
								  ]
								: []),
							...(fundType.fundInformations?.planning?.includes('capital_call_end_date')
								? [
										{
											id: 'capital_call_end_date',
											label: t(investmentFundInformationsLang.item_capital_call_end_date.tag),
											value: (
												<AppFormatDate date={fundInformations.planning?.capitalCallEndDate} />
											),
										},
								  ]
								: []),
							...(fundType.fundInformations?.planning?.includes('closing_dates') &&
							fundInformations.planning?.closingDates
								? fundInformations.planning.closingDates.map((x) => ({
										id: 'closing_dates',
										label: t(investmentFundInformationsLang.item_closing_dates_x.tag, {
											count: x.position,
										}),
										value: <AppFormatDate date={x.closingDate} />,
								  }))
								: []),
							...(fundType.fundInformations?.planning?.includes('investment_end_date')
								? [
										{
											id: 'investment_end_date',
											label: t(investmentFundInformationsLang.item_investment_end_date.tag),
											value: (
												<AppFormatDate date={fundInformations.planning?.investmentEndDate} />
											),
										},
								  ]
								: []),
							...(fundType.fundInformations?.planning?.includes('amortization_end_date')
								? [
										{
											id: 'amortization_end_date',
											label: t(investmentFundInformationsLang.item_amortization_end_date.tag),
											value: (
												<AppFormatDate date={fundInformations.planning?.amortizationEndDate} />
											),
										},
								  ]
								: []),
							...(fundType.fundInformations?.planning?.includes('liquidation_date') &&
							fundInformations.planning?.liquidationDate
								? [
										{
											id: 'liquidation_date',
											label: t(investmentFundInformationsLang.item_liquidation_date.tag),
											value: <AppFormatDate date={fundInformations.planning?.liquidationDate} />,
										},
								  ]
								: []),
						]}
					/>
				);
			case 'labels':
				return (
					fund.labels &&
					fund.labels.length > 0 && (
						<CFundInformationBlock
							id="fund-information-labels-block"
							title={t(investmentFundInformationsLang.title_labels.tag)}
							icon="fund_informations_labels"
							items={fund.labels.reduce<
								{ id: string; label: string; value: React.ReactNode; inline?: boolean }[]
							>((labels, label) => {
								if (INVEST_FUND_TAG_LIST.includes(label)) {
									labels.push({
										id: label,
										label: label.toUpperCase(),
										value: (
											<Box
												component="img"
												src={`/images/labels/label_${label}.png`}
												sx={sxImgLabels}
											/>
										),
										inline: true,
									});
								}
								return labels;
							}, [])}
						/>
					)
				);
			case 'roles':
				return (
					<CFundInformationBlock
						id="fund-information-roles-block"
						title={t(investmentFundInformationsLang.title_roles.tag)}
						icon="fund_informations_roles"
						items={[
							...(fundType.fundInformations?.roles?.includes('management_company')
								? [
										{
											id: 'management_company',
											label: t(investmentFundInformationsLang.item_management_company.tag),
											value: fundInformations.roles.managementCompany
												? fundInformations.roles.managementCompany +
												  `${
														fundInformations.roles.managementCompany ===
														'Sienna Private Credit'
															? ' - AMF n° GP97118'
															: ''
												  }`
												: fundInformations.roles.consultingCompany ??
												  t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.roles?.includes('valorizer')
								? [
										{
											id: 'depository',
											label: t(investmentFundInformationsLang.item_valorizer.tag),
											value:
												fundInformations.roles.fundAdministrator ??
												t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.roles?.includes('consulting_company')
								? [
										{
											id: 'consulting_company',
											label: t(investmentFundInformationsLang.item_consulting_company.tag),
											value: fundInformations.roles.consultingCompany
												? fundInformations.roles.consultingCompany +
												  `${
														fundInformations.roles.consultingCompany ===
														'Sienna Private Credit'
															? ' - AMF n° GP97118'
															: ''
												  }`
												: t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.roles?.includes('fund_administrator')
								? [
										{
											id: 'fund_administrator',
											label: t(investmentFundInformationsLang.item_fund_administrator.tag),
											value:
												fundInformations.roles.fundAdministrator ??
												t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.roles?.includes('depository')
								? [
										{
											id: 'depository',
											label: t(investmentFundInformationsLang.item_depository.tag),
											value: fundInformations.roles.depository ?? t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.roles?.includes('accountants')
								? [
										{
											id: 'accountants',
											label: t(investmentFundInformationsLang.item_accountants.tag),
											value: fundInformations.roles.accountants ?? t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
						]}
					/>
				);
			case 'strategy':
				return (
					<CFundInformationBlock
						id="fund-information-strategy-block"
						title={t(investmentFundInformationsLang.title_strategy.tag)}
						icon="fund_informations_strategy"
						items={[
							...(fundType.fundInformations?.strategy?.includes('asset_class')
								? [
										{
											id: 'strategy_asset_class',
											label: t(investmentFundInformationsLang.item_asset_class.tag),
											value: (() => {
												const k =
													`key_asset_class_${fundInformations.strategy.assetClass}` as keyof typeof investmentFundInformationsLang;
												return investmentFundInformationsLang[k]
													? t(investmentFundInformationsLang[k].tag)
													: fundInformations.strategy.assetClass ??
															t(langInvestWordLang.nc.tag);
											})(),
										},
								  ]
								: []),
							...(fundType.fundInformations?.strategy?.includes('domainTag')
								? [
										{
											id: 'domainTag',
											label: t(investmentFundInformationsLang.item_domain.tag),
											value:
												fundInformations.strategy && fundInformations.strategy.domainTag
													? t(
															investmentTypeDomainLang[
																fundInformations.strategy
																	.domainTag as keyof typeof investmentTypeDomainLang
															].tag,
													  )
													: fundInformations.strategy.domainTag ??
													  t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.strategy?.includes('domainText')
								? [
										{
											id: 'domainText',
											label: t(investmentFundInformationsLang.item_domain.tag),
											value:
												fundInformations.strategy && fundInformations.strategy.domainText
													? fundInformations.strategy.domainText
													: fundInformations.strategy.domainText ??
													  t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.strategy?.includes('sfdr')
								? [
										{
											id: 'sfdr',
											label: t(investmentFundInformationsLang.item_sfdr.tag),
											value:
												fundInformations.strategy && fundInformations.strategy.sfdr
													? investmentTypeArticleLang[
															fundInformations.strategy
																.sfdr as keyof typeof investmentTypeArticleLang
													  ]
														? t(
																investmentTypeArticleLang[
																	fundInformations.strategy
																		.sfdr as keyof typeof investmentTypeArticleLang
																].tag,
														  )
														: fundInformations.strategy.sfdr ?? t(langInvestWordLang.nc.tag)
													: fundInformations.strategy.sfdr ?? t(langInvestWordLang.na.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.strategy?.includes('thematic')
								? [
										{
											id: 'strategy_thematic',
											label: t(investmentFundInformationsLang.item_thematic.tag),
											value:
												fundInformations.strategy.sgeThematic || t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
							...(fundType.fundInformations?.strategy?.includes('investment_horizon')
								? [
										{
											id: 'strategy_investment_horizon',
											label: t(investmentFundInformationsLang.item_investment_horizon.tag),
											value:
												fundInformations?.strategy?.placementDuration ||
												t(langInvestWordLang.nc.tag),
										},
								  ]
								: []),
						]}
					/>
				);
		}
	};
	return (
		<>
			<CAppSubTitle
				title={t(investmentFundLang.title_informations.tag)}
				icon={<AppCustomIcon tag="fund_informations" className="icon" />}
			/>
			<Grid container spacing={3}>
				{fund &&
					fundInformations &&
					fundType &&
					(Object.keys(fundType.fundInformations) as KFundInformations[]).map((x) => {
						const block = getFundInformationBlock(x);
						return (
							block && (
								<Grid item sm={12} md={6} lg={3} key={x}>
									{block}
								</Grid>
							)
						);
					})}
			</Grid>
		</>
	);
};

export default VInvestFundInformations;
